import { BranchesOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isLayoutBlockType,
  type RepeatableColumn,
  type SelectOption,
  type WorkflowField,
  type WorkflowStep,
} from '@/api/workflows';

import { describeCondition, resolveFieldLabel } from './conditionSummary';
import { fieldDisplayLabelKey, metaForFieldType } from './form-builder/fieldMeta';

const { Text } = Typography;

/** Enum → i18n suffix maps, all under `workflow.builder.properties.*` so the
 *  read-only view reads the SAME labels the originator sees in the editor. */
const INPUT_RESTRICTION_KEY: Record<string, string> = {
  digits: 'inputRestrictionDigits',
  letters: 'inputRestrictionLetters',
  alphanumeric: 'inputRestrictionAlphanumeric',
};
const VALIDATE_KEY: Record<string, string> = {
  sin: 'validateSin',
  email: 'validateEmail',
  phone: 'validatePhone',
  postal: 'validatePostal',
  regex: 'validateRegex',
};
const NUMBER_FORMAT_KEY: Record<string, string> = {
  currency: 'numberFormatCurrency',
  percentage: 'numberFormatPercentage',
};
const LIST_STYLE_KEY: Record<string, string> = {
  disc: 'listStyleDisc',
  decimal: 'listStyleDecimal',
  dash: 'listStyleDash',
  checkmark: 'listStyleCheckmark',
};
const FONT_WEIGHT_KEY: Record<string, string> = {
  thin: 'weightThin',
  regular: 'weightRegular',
  bold: 'weightBold',
};
const ADDRESS_ROLE_KEY: Record<string, string> = {
  street: 'addressRoleStreet',
  city: 'addressRoleCity',
  province: 'addressRoleProvince',
  postal: 'addressRolePostal',
  apt: 'addressRoleApt',
};
const SOURCE_VALUE_KEY: Record<string, string> = {
  'advisor.fullName': 'sourceValueFullName',
  'advisor.email': 'sourceValueEmail',
  'advisor.phone': 'sourceValuePhone',
  'advisor.dealerCode': 'sourceValueDealerCode',
  'advisor.repCode': 'sourceValueRepCode',
};
const IMAGE_WIDTH_KEY: Record<string, string> = {
  small: 'imageWidthSmall',
  medium: 'imageWidthMedium',
  full: 'imageWidthFull',
};
const IMAGE_ALIGN_KEY: Record<string, string> = {
  left: 'imageAlignLeft',
  center: 'imageAlignCenter',
  right: 'imageAlignRight',
};
const NOTICE_ICON_KEY: Record<string, string> = {
  info: 'noticeIconInfo',
  warning: 'noticeIconWarning',
  success: 'noticeIconSuccess',
  tip: 'noticeIconTip',
};

/**
 * Read-only render of a single field's full authoring properties — the content
 * of the builder-style properties rail on {@link StepConfigPage}. Property
 * coverage mirrors `buildProperties` in the editor's FieldPropertiesPanel;
 * labels reuse the same `workflow.builder.properties.*` i18n keys the editor
 * uses. Purely presentational — no inputs, no mutation.
 */
export function ReadOnlyFieldProperties({
  field,
  allSteps,
  isFr,
}: {
  field: WorkflowField;
  allSteps: WorkflowStep[];
  isFr: boolean;
}) {
  const { t } = useTranslation();
  const p = (key: string) => t(`workflow.builder.properties.${key}`);
  const meta = metaForFieldType(field.type);
  const isLayout = isLayoutBlockType(field.type);
  // Layout blocks re-purpose the label as their rendered content.
  const primaryLabel = field.labelEn || (isLayout ? p('contentEn') : field.type);

  // Simple scalar rows — pushed only when the property is actually set so the
  // panel shows exactly what was authored (mirrors buildProperties' per-type set).
  const rows: Array<{ label: ReactNode; value: ReactNode }> = [];
  const push = (label: ReactNode, value: ReactNode) => rows.push({ label, value });
  // Map an enum value to its editor i18n label, falling back to the raw value.
  const mapped = (map: Record<string, string>, raw?: string | null) =>
    raw ? (map[raw] ? p(map[raw]!) : raw) : null;

  if (field.labelFr) push(p(isLayout ? 'contentFr' : 'labelFr'), field.labelFr);
  if (field.placeholder) push(p('placeholder'), field.placeholder);
  if (field.minLength != null) push(p('minLength'), field.minLength);
  if (field.maxLength != null) push(p('maxLength'), field.maxLength);
  if (field.min != null) push(p('min'), field.min);
  if (field.max != null) push(p('max'), field.max);
  if (field.integerOnly) push(p('integerOnly'), t('common.yes'));
  if (field.inputRestriction)
    push(p('inputRestriction'), mapped(INPUT_RESTRICTION_KEY, field.inputRestriction));
  if (field.validate) push(p('validate'), mapped(VALIDATE_KEY, field.validate));
  if (field.validate === 'regex' && field.regexPattern)
    push(p('regexPattern'), <code>{field.regexPattern}</code>);
  if (field.validate === 'regex' && field.regexMessageEn)
    push(p('regexMessageEn'), field.regexMessageEn);
  if (field.validate === 'regex' && field.regexMessageFr)
    push(p('regexMessageFr'), field.regexMessageFr);
  if (field.numberFormat) push(p('numberFormat'), mapped(NUMBER_FORMAT_KEY, field.numberFormat));
  if (field.minAge != null) push(p('minAge'), field.minAge);
  if (field.addressAutocomplete === 'canada-post')
    push(p('addressAutocompleteCanadaPost'), t('common.yes'));
  if (field.addressRole) push(p('addressRoleLabel'), mapped(ADDRESS_ROLE_KEY, field.addressRole));
  if (field.maxRows != null) push(p('maxRows'), field.maxRows);
  if (field.verticalOptions) push(p('verticalOptions'), t('common.yes'));
  if (field.blockingValues && field.blockingValues.length > 0) {
    push(p('blockingValue'), field.blockingValues.join(', '));
    if (field.type === 'CheckboxGroup')
      push(
        p('blockingValueOperator'),
        p(
          field.blockingValueOperator === 'and'
            ? 'blockingValueOperatorAnd'
            : 'blockingValueOperatorOr',
        ),
      );
  }
  if (field.blockContinueAlert) push(p('blockContinueAlert'), t('common.yes'));
  if (field.blockContinueMessageEn) push(p('blockContinueMessageEn'), field.blockContinueMessageEn);
  if (field.blockContinueMessageFr) push(p('blockContinueMessageFr'), field.blockContinueMessageFr);
  if (field.sourceValue) push(p('sourceValueLabel'), mapped(SOURCE_VALUE_KEY, field.sourceValue));
  if (field.uniquenessGroup) push(p('uniquenessGroupLabel'), field.uniquenessGroup);
  if (typeof field.minSelections === 'number')
    push(p('minSelectionsLabel'), String(field.minSelections));
  if (field.pdfMappings && field.pdfMappings.length > 0)
    push(
      p('internalName'),
      <code>{field.pdfMappings.map((m) => m.acroFieldName).join(', ')}</code>,
    );
  // Layout-block styling
  if (field.fontSize != null) push(p('fontSize'), `${field.fontSize} px`);
  if (field.fontWeight) push(p('fontWeight'), mapped(FONT_WEIGHT_KEY, field.fontWeight));
  if (field.color) push(p('color'), <Swatch hex={field.color} />);
  if (field.backgroundColor) push(p('backgroundColor'), <Swatch hex={field.backgroundColor} />);
  if (field.icon) push(p('noticeIcon'), mapped(NOTICE_ICON_KEY, field.icon));
  if (field.marginTop != null) push(p('marginTop'), `${field.marginTop} px`);
  if (field.marginBottom != null) push(p('marginBottom'), `${field.marginBottom} px`);
  if (field.listStyle) push(p('listStyle'), mapped(LIST_STYLE_KEY, field.listStyle));
  // Image
  if (field.imageWidthSize)
    push(p('imageWidthSize'), mapped(IMAGE_WIDTH_KEY, field.imageWidthSize));
  if (field.imageAlignment)
    push(p('imageAlignment'), mapped(IMAGE_ALIGN_KEY, field.imageAlignment));
  if (field.imageUrl)
    push(p('imageSectionTitle'), <code style={{ wordBreak: 'break-all' }}>{field.imageUrl}</code>);
  if (field.captionEn) push(p('imageCaptionEn'), field.captionEn);
  if (field.captionFr) push(p('imageCaptionFr'), field.captionFr);
  if (field.altEn) push(p('imageAltEn'), field.altEn);
  if (field.altFr) push(p('imageAltFr'), field.altFr);
  // Hint + required message
  if (field.hintEn) push(p('hintEn'), field.hintEn);
  if (field.hintFr) push(p('hintFr'), field.hintFr);
  if (field.requiredMessageEn) push(p('requiredMessageEn'), field.requiredMessageEn);
  if (field.requiredMessageFr) push(p('requiredMessageFr'), field.requiredMessageFr);

  const sourceField = field.sourceField;
  const conditions = field.conditions ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Header: field name on top, type chip + flag badges underneath so a
          long label isn't crowded by the tags. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Text strong style={{ fontSize: 14, color: '#111827', wordBreak: 'break-word' }}>
          {primaryLabel}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <Tag
            style={{
              marginInlineEnd: 0,
              background: meta.tintBg,
              color: meta.tintFg,
              border: `1px solid ${meta.tintFg}33`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            icon={meta.icon()}
          >
            {t(fieldDisplayLabelKey(field))}
          </Tag>
          {field.required && <Tag color="red">{p('required')}</Tag>}
          {field.readOnly && <Tag>{p('readOnly')}</Tag>}
          {field.hideLabel && <Tag>{t('workflow.review.config.hideLabel')}</Tag>}
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {r.label}
              </Text>
              <div style={{ fontSize: 13, color: '#111827', minWidth: 0, wordBreak: 'break-word' }}>
                {r.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Options (SingleSelect / RadioGroup / CheckboxGroup / BulletedList) */}
      {field.options?.length > 0 && (
        <OptionsTable options={field.options} type={field.type} allSteps={allSteps} isFr={isFr} />
      )}

      {/* RepeatableRow column template */}
      {field.repeatableTemplate?.length ? (
        <TemplateTable columns={field.repeatableTemplate} />
      ) : null}

      {/* Cross-row sum rule */}
      {field.sumValidation && (
        <SubBlock title={p('sumValidationTitle')}>
          <Text style={{ fontSize: 13 }}>
            {p('sumValidationColumn')}: <code>{field.sumValidation.columnKey}</code> ·{' '}
            {field.sumValidation.targetConstant != null
              ? `${p('sumValidationTargetConstant')} = ${field.sumValidation.targetConstant}`
              : `${p('sumValidationTargetField')}: ${resolveFieldLabel(
                  field.sumValidation.targetFieldId ?? '',
                  allSteps,
                  isFr,
                )}`}
          </Text>
        </SubBlock>
      )}

      {/* Cross-step prefill */}
      {sourceField && (
        <SubBlock title={p('sourceFieldTitle')}>
          <Text style={{ fontSize: 13 }}>
            {resolveFieldLabel(sourceField.fieldId, allSteps, isFr)}
          </Text>
        </SubBlock>
      )}

      {/* Help bubble */}
      {field.helpTitleEn && (
        <SubBlock title={p('helpSectionTitle')}>
          <div style={{ fontSize: 13 }}>
            <Text strong>{field.helpTitleEn}</Text>
            {field.helpBodyEn && (
              <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{field.helpBodyEn}</div>
            )}
            {field.helpTitleFr && (
              <div style={{ marginTop: 6, color: '#6b7280' }}>
                <Text strong style={{ color: '#6b7280' }}>
                  {field.helpTitleFr}
                </Text>
                {field.helpBodyFr && (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{field.helpBodyFr}</div>
                )}
              </div>
            )}
          </div>
        </SubBlock>
      )}

      {/* Field-level visibility conditions */}
      {conditions.length > 0 && (
        <ConditionBlock
          combinator={field.conditionCombinator}
          summaries={conditions.map((c) => describeCondition(c, allSteps, isFr))}
        />
      )}
    </div>
  );
}

function OptionsTable({
  options,
  type,
  allSteps,
  isFr,
}: {
  options: SelectOption[];
  type: WorkflowField['type'];
  allSteps: WorkflowStep[];
  isFr: boolean;
}) {
  const { t } = useTranslation();
  const p = (key: string) => t(`workflow.builder.properties.${key}`);
  const isBulleted = type === 'BulletedList';
  return (
    <SubBlock title={p('options')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {options.map((o, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              borderBottom: i < options.length - 1 ? '1px dashed #eef2f7' : undefined,
              paddingBottom: 4,
            }}
          >
            {!isBulleted && (
              <Tag style={{ marginInlineEnd: 0, fontFamily: 'monospace' }}>{o.value}</Tag>
            )}
            <Text style={{ fontSize: 13 }}>{o.labelEn}</Text>
            {o.labelFr && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                / {o.labelFr}
              </Text>
            )}
            {o.helpTitleEn && <Tag color="blue">{t('workflow.review.config.hasHelp')}</Tag>}
            {o.clearsOnSelect?.length ? (
              <Tag color="orange">
                {t('workflow.review.config.clears', { list: o.clearsOnSelect.join(', ') })}
              </Tag>
            ) : null}
            {o.revealFieldId && (
              <Tag color="geekblue">
                {t('workflow.review.config.reveals', {
                  label: resolveFieldLabel(o.revealFieldId, allSteps, isFr),
                })}
              </Tag>
            )}
            {o.pdfMappings && o.pdfMappings.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {p('internalName')}:{' '}
                <code>{o.pdfMappings.map((m) => m.acroFieldName).join(', ')}</code>
              </Text>
            )}
          </div>
        ))}
      </div>
    </SubBlock>
  );
}

function TemplateTable({ columns }: { columns: RepeatableColumn[] }) {
  const { t } = useTranslation();
  const p = (key: string) => t(`workflow.builder.properties.${key}`);
  return (
    <SubBlock title={p('repeatableTemplate')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {columns.map((c) => (
          <div
            key={c.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
            }}
          >
            <Text strong style={{ fontSize: 13 }}>
              {c.labelEn || '—'}
            </Text>
            {c.labelFr && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                / {c.labelFr}
              </Text>
            )}
            <Tag style={{ marginInlineEnd: 0 }}>{t(metaForFieldType(c.type).labelKey)}</Tag>
            {c.required && <Tag color="red">{p('required')}</Tag>}
            {c.widthPercent != null && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {p('columnWidth')}: {c.widthPercent}%
              </Text>
            )}
            {c.maxLength != null && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {p('maxLength')}: {c.maxLength}
              </Text>
            )}
            {(c.helpEn || c.helpFr) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {p('columnHelpEn')}: {c.helpEn || c.helpFr}
              </Text>
            )}
            {c.internalName && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {p('internalName')}: <code>{c.internalName}</code>
              </Text>
            )}
          </div>
        ))}
      </div>
    </SubBlock>
  );
}

function ConditionBlock({
  combinator,
  summaries,
}: {
  combinator?: 'all' | 'any' | null;
  summaries: string[];
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        border: '1px solid #bfdbfe',
        background: '#eff6ff',
        borderRadius: 8,
        padding: '8px 10px',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          fontWeight: 600,
          color: '#1d4ed8',
          marginBottom: 4,
        }}
      >
        <BranchesOutlined />
        {t('approver.review.conditionsPeekTitle', { defaultValue: 'Visibility conditions' })}
        {combinator ? ` · ${t(`workflow.review.config.combinator.${combinator}`)}` : ''}
      </div>
      <ul style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 2 }}>
        {summaries.map((s, i) => (
          <li key={i}>
            <Text style={{ fontSize: 13 }}>{s}</Text>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SubBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ borderTop: '1px dashed #eef2f7', paddingTop: 8 }}>
      <Text
        type="secondary"
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          display: 'block',
          marginBottom: 6,
        }}
      >
        {title}
      </Text>
      {children}
    </div>
  );
}

function Swatch({ hex }: { hex: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          background: hex,
          border: '1px solid #d1d5db',
          display: 'inline-block',
        }}
      />
      <code>{hex}</code>
    </span>
  );
}
